import { tg } from '@infotech-soe/metaschema';
import { DigitalSignService } from '@infotech-soe/mia-ds-service';
import {
  Authority,
  DSAgents,
  DSDevices,
  IDigitalSignService,
  KeyTypes,
} from '@infotech-soe/mia-ds-service/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

type UseSignResult = {
  authorities: Authority[];
  init: boolean;
  loading: boolean;
  signDevices: DSDevices;
  onRefreshDevices: () => Promise<void>;
  onSign: IDigitalSignService['sign'];
};

export function useSign(
  dsAgent: DSAgents,
  dsKeyTypes: KeyTypes[],
  allowedTypes: string | null,
  forbiddenTypes: string | null,
  onError: (e: Error) => void
): UseSignResult {
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [signDevices, setSignDevices] = useState<DSDevices>([]);

  const SignService = useMemo(() => new DigitalSignService(), []);
  const filterDevices = useCallback(
    (devices: DSDevices) => {
      return devices.filter(({ name }) => {
        if (tg.isSomething(forbiddenTypes)) {
          const blackListExp = new RegExp(forbiddenTypes);
          if (blackListExp instanceof RegExp && blackListExp.test(name))
            return false;
        } else if (tg.isSomething(allowedTypes)) {
          const whiteListExp = new RegExp(allowedTypes);
          if (whiteListExp instanceof RegExp && !whiteListExp.test(name))
            return false;
        }
        return true;
      });
    },
    [allowedTypes, forbiddenTypes]
  );

  useEffect(() => {
    async function initialize() {
      try {
        let sertCenters: Authority[] = [];

        setLoading(true);
        await SignService.initialize(dsAgent);
        if (dsAgent === DSAgents.CIPHER) {
          sertCenters = await SignService.refreshAuthorities(dsAgent);
          setAuthorities(sertCenters);
        }
        if (
          dsAgent === DSAgents.CIPHER ||
          (dsKeyTypes.length === 1 && dsKeyTypes[0] === KeyTypes.DEVICE)
        ) {
          const devices = await SignService.refreshDevices(dsAgent);
          setSignDevices(filterDevices(devices));
        }
        setInit(
          dsAgent === DSAgents.IIT ||
            (dsAgent === DSAgents.CIPHER && sertCenters.length > 0)
        );
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.log(e);
      } finally {
        setLoading(false);
      }
    }

    void initialize();
  }, [SignService, dsAgent, dsKeyTypes, filterDevices]);

  const onRefreshDevices = useCallback(async () => {
    try {
      setLoading(true);
      const devices = await SignService.refreshDevices(dsAgent);
      setSignDevices(filterDevices(devices));
      setLoading(false);
    } catch (e: any) {
      onError(e);
    }
  }, [SignService, dsAgent, filterDevices, onError]);

  const handleSign = useCallback<IDigitalSignService['sign']>(
    (...args) => SignService.sign(...args),
    [SignService]
  );

  return useMemo(
    () => ({
      authorities,
      init,
      loading,
      signDevices,
      onRefreshDevices,
      onSign: handleSign,
    }),
    [authorities, handleSign, init, loading, onRefreshDevices, signDevices]
  );
}
